import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { callFn } from '../../lib/api';
import type { Reservation } from '../../lib/types';
import { reservationStatusLabel, tagNames } from '../../lib/types';
import { useApp } from '../../app/AppContext';
import { DataTable, type Column } from '../../components/DataTable';
import { useLightbox, ZoomThumb } from '../../components/Lightbox';

type RowAction = { label: string; run: () => void; danger?: boolean };

// Per-row "Actions ▾" menu — keeps the status-dependent controls (mark ready,
// picked up, release, cancel, tags) tidy in one dropdown instead of a wrapping
// wall of links. The menu is portalled to <body> with fixed positioning so the
// table's `overflow:hidden` (rounded corners) can't clip it on the last rows.
// Top-level so it isn't remounted on every parent render.
function RowActionsMenu({ items }: { items: RowAction[] }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  if (items.length === 0) return <span className="muted" style={{ fontSize: '.8rem' }}>—</span>;

  return (
    <>
      <button ref={btnRef} className="btn ghost small" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        Actions ▾
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} className="row-actions-menu" role="menu"
          style={{ position: 'fixed', top: pos.top, right: pos.right }}>
          {items.map((it, i) => (
            <button key={i} role="menuitem" className={`row-actions-item${it.danger ? ' danger' : ''}`}
              onClick={() => { setOpen(false); it.run(); }}>
              {it.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

export function Allocation() {
  const { toast } = useApp();
  const lb = useLightbox();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = params.get('user') || '';
  const userName = params.get('name') || '';
  const statusFilter = params.get('status') ?? '';

  function setStatus(s: string) {
    const next = new URLSearchParams(params);
    if (s) next.set('status', s); else next.delete('status');
    setParams(next, { replace: true });
  }
  function clearUser() {
    const next = new URLSearchParams(params);
    next.delete('user'); next.delete('name');
    setParams(next, { replace: true });
  }

  async function load() {
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {};
      if (statusFilter) payload.status = statusFilter;
      if (userId) payload.user_id = userId;
      const res = await callFn<Reservation[]>('list_reservations', payload);
      setRows(Array.isArray(res) ? res : []);
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed to load', 'err'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [statusFilter, userId]);

  async function act(action: string, id: string, label: string, confirmMsg?: string, extra?: Record<string, unknown>) {
    if (confirmMsg && !confirm(confirmMsg)) return;
    try { await callFn(action, { id, ...(extra || {}) }); toast(label, 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  // Nudge a reservation's quantity by ±1 (moves item stock). Update just this
  // row from the server's response so the table doesn't flash/reload.
  async function changeQty(r: Reservation, delta: number) {
    try {
      const upd = await callFn<Reservation>('adjust_reservation_quantity', { id: r.id, delta });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, quantity: upd.quantity } : x)));
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not change quantity', 'err'); }
  }

  const columns: Column<Reservation>[] = [
    { key: 'location', label: 'Location', get: (r) => r.location_name || 'Unassigned' },
    {
      key: 'item', label: 'Item', get: (r) => r.item_description || '',
      render: (r) => (
        <span className="cell-with-thumb">
          {r.photo_ref
            ? <ZoomThumb src={r.photo_ref} className="row-thumb" caption={r.item_description} onOpen={lb.open} />
            : <span className="row-thumb empty">🐾</span>}
          {r.item_description}
        </span>
      ),
    },
    {
      key: 'qty', label: 'Qty', get: (r) => r.quantity, numeric: true, align: 'right',
      render: (r) => (['reserved', 'ready'].includes(r.status)
        ? <div className="qty-stepper">
            <button className="qty-btn" title="Decrease" disabled={r.quantity <= 1} onClick={() => changeQty(r, -1)}>−</button>
            <span className="qty-val">{r.quantity}</span>
            <button className="qty-btn" title="Increase" onClick={() => changeQty(r, 1)}>+</button>
          </div>
        : <span>{r.quantity}</span>),
    },
    { key: 'foster', label: 'Foster', get: (r) => r.foster_name || r.foster_email || '' },
    {
      key: 'status', label: 'Status', get: (r) => r.status,
      render: (r) => <span className={`badge ${r.status}`}>{reservationStatusLabel(r.status)}</span>,
    },
    {
      key: 'tags', label: 'Tags',
      // The item's tags (read-only here; managed on Catalog). Semicolon-joined
      // for sort / filter / CSV.
      get: (r) => tagNames(r.tags),
      render: (r) => (r.tags && r.tags.length > 0
        ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {r.tags.map((t) => <span key={t.id} className="tag-chip" style={{ background: t.color || '#dce6f1' }}>{t.name}</span>)}
          </div>
        : <span className="muted">—</span>),
    },
    { key: 'reserved', label: 'Reserved', get: (r) => r.reserved_at, render: (r) => new Date(r.reserved_at).toLocaleDateString() },
  ];

  return (
    <>
      <h1 className="page-title">Allocation by pickup location</h1>
      <p className="page-sub">What to drop where. Mark items picked up, cancel, or release overdue ones back to the pool.</p>

      <div className="toolbar">
        <select value={statusFilter} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="reserved">Reserved</option>
          <option value="ready">Ready to be picked up</option>
          <option value="picked_up">Picked up</option>
          <option value="cancelled">Cancelled</option>
          <option value="released">Released</option>
        </select>
        <button className="btn secondary small" onClick={() => void load()}>Refresh</button>
        {userId && <span className="notice info" style={{ margin: 0, padding: '6px 12px' }}>Showing: <b>{userName || 'one foster'}</b> <button className="btn-link" onClick={clearUser}>clear</button></span>}
      </div>

      {loading ? <div className="loading">Loading…</div> : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          initialSort={{ key: 'location', dir: 'asc' }}
          exportName="allocation"
          empty="No reservations to show."
          actions={(r) => {
            const active = r.status === 'reserved' || r.status === 'ready' || r.status === 'picked_up';
            const items: RowAction[] = [];
            if (r.status === 'reserved') items.push({ label: '🎁 Mark ready to be picked up', run: () => act('mark_ready', r.id, 'Marked ready for pickup') });
            if (r.status === 'reserved' || r.status === 'ready') items.push({ label: '✅ Mark picked up', run: () => act('mark_pickup', r.id, 'Marked picked up') });
            if (active) items.push({ label: '↩️ Release to pool', run: () => act('release_reservation', r.id, 'Released to pool', 'Release this item back into the pool?') });
            const who = r.foster_name || r.foster_email;
            if (active) items.push({ label: '✖️ Cancel — return to pool', danger: true, run: () => act('admin_cancel_reservation', r.id, 'Reservation cancelled', `Cancel this reservation for ${who}? The item goes back into the pool for others to reserve.`) });
            if (active) items.push({ label: '🚫 Cancel — don\'t return to pool', danger: true, run: () => act('admin_cancel_reservation', r.id, 'Reservation cancelled (not returned to pool)', `Cancel this reservation for ${who} WITHOUT putting the item back in the pool? Use this when the item is gone (already handed over, lost or damaged).`, { restock: false }) });
            if (r.status === 'cancelled' || r.status === 'released') items.push({ label: '♻️ Reinstate reservation', run: () => act('reinstate_reservation', r.id, 'Reservation reinstated', `Reinstate this reservation for ${who}? It goes back to Reserved and the item is taken out of the pool again. You can then mark it picked up.`) });
            return <RowActionsMenu items={items} />;
          }}
        />
      )}
      {lb.node}
    </>
  );
}
